import { Badge } from "@solomon-apps/shared/src/components/ui/badge";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import type React from "react";

import {
    formatBound,
    PERMISSION_LABELS,
    type PolicyConstraint,
    type PolicyPermission
} from "./policiesMocks";

export interface ConstraintSummaryProps {
    permission: PolicyPermission;
    constraint: PolicyConstraint;
    minViolated: boolean;
    maxViolated: boolean;
    className?: string;
}

/**
 * Issuer constraints row — the permission badge plus, for range constraints
 * on non-delegated policies, the Minimum and Maximum (or "No maximum") bounds.
 * Min/Max highlight red when the matching `*Violated` flag is set. Purely
 * presentational; violation flags are computed by the consumer.
 */
export function ConstraintSummary({
    permission,
    constraint,
    minViolated,
    maxViolated,
    className
}: ConstraintSummaryProps): React.ReactElement {
    // No issuer ceiling — a floor-only constraint. Drives the "No maximum"
    // label.
    const noMax =
        constraint.kind === "range" && !Number.isFinite(constraint.max);

    return (
        <div
            className={`flex flex-wrap items-center gap-x-6 gap-y-2${
                className ? ` ${className}` : ""
            }`}
        >
            <Badge variant="quaternary">{PERMISSION_LABELS[permission]}</Badge>
            {permission === "delegated" ? null : constraint.kind === "range" ? (
                <>
                    <Text
                        variant="small"
                        render={<span />}
                        className={
                            minViolated
                                ? "text-error-base"
                                : "text-foreground-base"
                        }
                    >
                        <span
                            className={
                                minViolated ? "" : "text-foreground-muted"
                            }
                        >
                            Minimum:{" "}
                        </span>
                        {formatBound(constraint, constraint.min)}
                    </Text>
                    {noMax ? (
                        <Text
                            variant="small"
                            render={<span />}
                            className="text-foreground-muted"
                        >
                            No maximum
                        </Text>
                    ) : (
                        <Text
                            variant="small"
                            render={<span />}
                            className={
                                maxViolated
                                    ? "text-error-base"
                                    : "text-foreground-base"
                            }
                        >
                            <span
                                className={
                                    maxViolated ? "" : "text-foreground-muted"
                                }
                            >
                                Maximum:{" "}
                            </span>
                            {formatBound(constraint, constraint.max)}
                        </Text>
                    )}
                </>
            ) : null}
        </div>
    );
}
